import { useState, useEffect, type ReactNode } from 'react'
import { ActionType, type Chart, type Crosshair, type KLineData } from 'klinecharts'
import type { EventBus } from '../core/EventBus'
import { barChangeTooltipEntry } from '../utils/ohlcChange'

export interface OhlcHeaderProps {
  chart: Chart | null
  eventBus: EventBus | null
  /**
   * Inline flow mode: rendered as a wrapping flex item inside the floating
   * toolbar — sits to the right of the chips when the row has room, wraps
   * underneath when it doesn't. `top`/`left` are ignored.
   */
  inline?: boolean
  /** Pixel offsets inside the chart-area container (absolute mode only). */
  top?: number
  left?: number
  /** Value tint follows the bar direction, matching the candles. */
  upColor: string
  downColor: string
  /** Append the change / change-% readout after C (daily timeframes). */
  showChange: boolean
  /** Content rendered at the start of the row, before the O/H/L/C values. */
  leading?: ReactNode
}

/**
 * React replacement for the canvas-drawn candle tooltip row ("O 2.28 H 2.30
 * L 2.27 C 2.30"). Living in the DOM lets the titles render light and the
 * VALUES bold — klinecharts' canvas tooltip only supports one font weight for
 * the whole row. Follows the crosshair bar, falling back to the latest bar.
 */
export function OhlcHeader({
  chart,
  eventBus,
  inline,
  top,
  left,
  upColor,
  downColor,
  showChange,
  leading,
}: OhlcHeaderProps) {
  const [bars, setBars] = useState<{ current: KLineData; prev: KLineData | null } | null>(null)

  useEffect(() => {
    if (!chart) return

    const compute = (dataIndex?: number) => {
      const dataList = chart.getDataList()
      if (dataList.length === 0) return
      const idx = dataIndex ?? dataList.length - 1
      if (idx < 0 || idx >= dataList.length) return
      setBars({ current: dataList[idx], prev: dataList[idx - 1] ?? null })
    }

    const onCrosshair = (crosshair: Crosshair) => {
      compute(crosshair.dataIndex ?? undefined)
    }

    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    // Latest-bar refresh while no crosshair is active, and a snap back to the
    // latest bar when the cursor leaves the chart area. `data:loaded` covers
    // history loads (initial mount AND in-place symbol swaps, e.g. scanner
    // ticker propagation); `data:updated` covers realtime bar ticks.
    const unsubLoaded = eventBus?.on('data:loaded', () => compute())
    const unsubData = eventBus?.on('data:updated', () => compute())
    const unsubLeave = eventBus?.on('crosshair:leave', () => compute())
    // Synced crosshair from a PEER chart (ChartManager.executeCrosshair maps
    // the source timestamp to this chart's bars): follow the mapped bar;
    // BLANK when the timestamp lands in a gap here (dataIndex null); back to
    // the latest bar when the peer clears (payload null).
    const unsubRemote = eventBus?.on('crosshair:remote', (payload) => {
      const p = payload as { dataIndex: number | null } | null
      if (p === null) compute()
      else if (p.dataIndex == null) setBars(null)
      else compute(p.dataIndex)
    })
    compute()

    return () => {
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
      unsubLoaded?.()
      unsubData?.()
      unsubLeave?.()
      unsubRemote?.()
    }
  }, [chart, eventBus])

  // No bar yet (or a gap under a peer crosshair): the leading content still
  // shows, so a chip there never blinks with the values.
  if (!bars && !leading) return null

  const values = bars ? ohlcValues(bars, chart, upColor, downColor, showChange) : null

  return (
    <div
      data-testid="ohlc-header"
      style={inline ? undefined : { top, left, zIndex: 2 }}
      className={
        'pointer-events-none flex select-none items-center gap-1.5 whitespace-nowrap text-[11px] leading-none ' +
        // Inline: a wrapping flex item. Kept shorter than the 22px chips
        // (items-center still aligns it beside them) so a wrapped second row
        // doesn't push the legend stack down further than the text needs.
        // pl-0.5: the toolbar strip sits at left 6px, so 2px of padding lands
        // the text at 8px — exactly where the indicator-legend labels start
        // (4px wrapper + 4px row padding).
        (inline ? 'h-4 pl-0.5 pr-1' : 'absolute px-1')
      }
    >
      {leading}
      {values?.entries.map((e) => (
        <span key={e.label}>
          <span className="font-normal text-[hsl(var(--chart-text-muted))]">{e.label} </span>
          <span className="font-normal" style={{ color: values.dirColor }}>
            {e.text}
          </span>
        </span>
      ))}
      {values?.change && (
        <span className="font-normal" style={{ color: values.change.value.color }}>
          {values.change.value.text}
        </span>
      )}
    </div>
  )
}

function ohlcValues(
  { current, prev }: { current: KLineData; prev: KLineData | null },
  chart: Chart | null,
  upColor: string,
  downColor: string,
  showChange: boolean,
) {
  const precision = chart?.getPriceVolumePrecision?.().price ?? 2
  const fmt = (v: number) => v.toFixed(precision)
  const dirColor = current.close >= current.open ? upColor : downColor
  // The change field colors by its OWN direction (vs the previous close, which
  // can differ from close-vs-open), but with the SAME candle up/down colors so
  // the whole readout stays one palette.
  const change = showChange
    ? barChangeTooltipEntry(prev, current, { up: upColor, down: downColor })
    : null

  const raw: Array<{ label: string; value: number }> = [
    { label: 'O', value: current.open },
    { label: 'H', value: current.high },
    { label: 'L', value: current.low },
    { label: 'C', value: current.close },
  ]

  const entries = raw.map((e) => ({ label: e.label, text: fmt(e.value) }))
  return { entries, dirColor, change }
}
